import { AlertTriangle, Check, Plug, Save, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import {
	btn,
	ErrorBanner,
	input,
	Panel,
	Spinner,
} from "../../components/flows/ui";
import type {
	MaskedSetting,
	SettingsResponse,
	TestResult,
} from "../../types/flows";

// Secrets never come back from the API — a secret field shows whether it is set
// and its last four characters, nothing more. Typing replaces it; clearing it
// deletes the row, which is also how a value falls back to the environment
// again.

const SecretField = ({
	setting,
	value,
	onChange,
}: {
	setting: MaskedSetting;
	value: string | undefined;
	onChange: (value: string) => void;
}) => (
	<label className="block space-y-1">
		<span className="block flex flex-wrap items-baseline gap-2 text-[12px] font-medium text-slate-600">
			{setting.label}
			<span className="font-mono text-[10px] font-normal text-slate-400">
				{setting.key}
			</span>
			{setting.isSet ? (
				<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
					set{setting.last4 ? ` · …${setting.last4}` : ""}
				</span>
			) : (
				<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
					not set
				</span>
			)}
			{setting.fromEnv && (
				<span
					className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] text-sky-700"
					title="This value comes from the environment, not from this page. Saving one here overrides it."
				>
					from environment
				</span>
			)}
		</span>
		<input
			type={setting.secret ? "password" : "text"}
			autoComplete="off"
			className={input}
			placeholder={
				setting.secret
					? setting.isSet
						? "Leave blank to keep, or type a new value"
						: (setting.placeholder ?? "")
					: (setting.placeholder ?? "")
			}
			value={value ?? (setting.secret ? "" : (setting.value ?? ""))}
			onChange={(event) => onChange(event.target.value)}
		/>
		{setting.hint && (
			<p className="text-[11px] text-slate-400">{setting.hint}</p>
		)}
	</label>
);

const FlowsSettings = () => {
	const [data, setData] = useState<SettingsResponse | null>(null);
	const [edits, setEdits] = useState<Record<string, string>>({});
	const [tests, setTests] = useState<Record<string, TestResult | "running">>(
		{},
	);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			setData(await fetchJson<SettingsResponse>("/flows/settings"));
			setEdits({});
		} catch (e) {
			setError(String(e));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const save = async () => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const result = await sendJson<{
				saved: string[];
				cleared: string[];
				error?: string;
			}>("/flows/settings", "PUT", { values: edits });
			if (result.error) setError(result.error);
			else {
				setNotice(
					`${result.saved.length} saved${
						result.cleared.length > 0
							? `, ${result.cleared.length} cleared`
							: ""
					}.`,
				);
				await load();
			}
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const test = async (group: string) => {
		setTests({ ...tests, [group]: "running" });
		try {
			const result = await sendJson<TestResult>(
				`/flows/settings/test/${group}`,
				"POST",
			);
			setTests((current) => ({ ...current, [group]: result }));
		} catch (e) {
			setTests((current) => ({
				...current,
				[group]: { ok: false, detail: String(e) },
			}));
		}
	};

	if (!data) return error ? <ErrorBanner error={error} /> : <Spinner />;

	const dirty = Object.keys(edits).length > 0;

	return (
		<div className="space-y-4">
			{!data.encryptionReady && (
				<div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-[13px] leading-relaxed text-danger">
					<ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
					<div>
						<p className="font-semibold">
							FLOWS_SECRET_KEY is not set, so no secret can be saved.
						</p>
						<p className="mt-0.5 text-slate-600">
							Every secret on this page is encrypted at rest with that key.
							Until it is set in the admin server's environment, saving a token
							here will fail — and any secret written under a previous key reads
							back as unset rather than crashing a run. Non-secret values still
							save normally.
						</p>
					</div>
				</div>
			)}

			<ErrorBanner error={error} />
			{notice && (
				<p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
					{notice}
				</p>
			)}

			{dirty && (
				<div className="sticky top-16 z-20 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
					<p className="text-[13px] text-slate-700">
						{Object.keys(edits).length} field
						{Object.keys(edits).length === 1 ? "" : "s"} changed. A field left
						blank is removed.
					</p>
					<button
						type="button"
						onClick={save}
						disabled={busy}
						className={`${btn.primary} ml-auto`}
					>
						<Save className="h-4 w-4" /> Save
					</button>
					<button
						type="button"
						onClick={() => setEdits({})}
						className={btn.ghost}
					>
						Discard
					</button>
				</div>
			)}

			{data.groups.map((group) => {
				const settings = data.settings.filter((s) => s.group === group.id);
				if (settings.length === 0) return null;
				const result = tests[group.id];

				return (
					<Panel
						key={group.id}
						title={group.label}
						action={
							group.testable ? (
								<button
									type="button"
									disabled={result === "running"}
									onClick={() => test(group.id)}
									className={btn.ghost}
								>
									<Plug className="h-4 w-4" />
									{result === "running" ? "Testing…" : "Test"}
								</button>
							) : undefined
						}
					>
						<p className="mb-4 max-w-3xl text-[12px] leading-relaxed text-slate-500">
							{group.description}
						</p>

						{result && result !== "running" && (
							<p
								className={`mb-4 flex items-start gap-1.5 rounded-lg px-3 py-2 text-[12px] ${
									result.ok
										? "bg-emerald-50 text-emerald-700"
										: "bg-danger/5 text-danger"
								}`}
							>
								{result.ok ? (
									<Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								) : (
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								)}
								{result.detail}
							</p>
						)}

						<div className="grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
							{settings.map((setting) => (
								<SecretField
									key={setting.key}
									setting={setting}
									value={edits[setting.key]}
									onChange={(value) =>
										setEdits({ ...edits, [setting.key]: value })
									}
								/>
							))}
						</div>
					</Panel>
				);
			})}
		</div>
	);
};

export default FlowsSettings;
